"use client";

import { c as _c } from "react/compiler-runtime";
import { X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { APP_INSTALL_PATH, badgeAttr } from "~/lib/constants/app-store";
import { isBannerHiddenPath, isCookieBannerShowing, isWithinDismissalWindow, readDismissedAt, writeDismissedAt } from "./banner-visibility";

/**
 * Mobile-web install banner, shaped like the native one people already know.
 * Mobile only: on desktop the badge surfaces do this job and there is no app to
 * open. Matters most for the campaign, where an SMS or a scanned link is opened
 * on a phone.
 *
 * "Get" points at `/app` rather than a store URL so the platform resolves
 * server side, which also keeps it to one link to measure.
 */
export function AppSmartBanner() {
  const $ = _c(9);
  const pathname = usePathname();
  const [dismissed, setDismissed] = useState(true);
  let t0;
  let t1;
  if ($[0] !== pathname) {
    t0 = () => {
      if (isBannerHiddenPath(pathname)) {
        return;
      }
      setDismissed(isCookieBannerShowing() || isWithinDismissalWindow(readDismissedAt(), Date.now()));
    };
    t1 = [pathname];
    $[0] = pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  if (dismissed || isBannerHiddenPath(pathname)) {
    return null;
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden={true} className="h-16 md:hidden" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span aria-hidden={true} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-(--r-md) bg-accent font-serif text-[16px] text-white">EZ</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="min-w-0"><span className="block font-ui text-[13.5px] font-bold leading-tight text-body">EZSpot</span><span className="block font-ui text-[11.5px] text-muted">Free · App Store and Google Play</span></span>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <a href={APP_INSTALL_PATH} data-attr={badgeAttr("universal", "mobile-banner")} className="ml-auto inline-flex h-11 shrink-0 items-center rounded-full bg-accent px-5 font-ui text-[12.5px] font-extrabold uppercase tracking-[0.03em] text-white transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">Get</a>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      writeDismissedAt(Date.now());
      setDismissed(true);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <>{t2}<aside aria-label="Get the EZSpot app" className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-border bg-surface px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-6px_20px_rgba(0,0,0,0.08)] md:hidden">{t3}{t4}{t5}<button type="button" aria-label="Dismiss" onClick={t6} className="-ml-2 order-first flex h-11 w-11 shrink-0 items-center justify-center text-muted"><X className="h-4 w-4" /></button></aside></>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}